import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  BodyText,
  Button,
  Card,
  Badge,
  DataGrid,
  Dialog,
  FilterBar,
  MetricCard,
  METRIC_READ_CAPTIONS,
  PageHeader,
  Skeleton,
} from '@aaimplatform/ui';
import type { ActiveFilter, ColumnDef, FilterDefinition } from '@aaimplatform/ui';
import {
  RefreshCw as RefreshIcon,
  Eye as ViewIcon,
  FileText as TitleIcon,
  AlertTriangle as WarningIcon,
} from 'lucide-react';
import { useTitleSearches, useCreateTitleSearch } from '../hooks';
import { useTenantNames } from '../hooks/useTenantNames';
import type { TenantNameState } from '../hooks/useTenantNames';
import type { TitleSearch, TitleSearchStatus } from '../api/client';
import {
  ABSENT,
  formatCount,
  formatDate,
  formatDateTime,
  formatMoney,
} from '../utils/display-format';
import {
  CollateralListEmptyState,
  LIEN_DATA_ABSENT,
  LIEN_DATA_REFUSAL_REASON,
  TITLE_SEARCH_STATUS_LABEL,
  TenantLoanCell,
  TitleSearchStatusBadge,
  formatLienCount,
  isMeasured,
  labelOptions,
  tenantDisplayName,
} from '../lib/collateral-display';
import type { CollateralEmptyCopyConfig } from '../lib/collateral-display';

/** The spec's maximum page. The counts below are taken over the rows read. */
const LIST_LIMIT = 100;

/**
 * What isInsurable and defectCount print when the record carries null: the
 * Reference Model did not report them (GH #3975). Neutral text, never "No" or
 * "0", which would assert a verdict and a clean title nobody measured.
 */
export const NOT_MEASURED = 'Not measured';

/** "Yes", "No", or {@link NOT_MEASURED} for an insurability the record omits. */
function insurabilityText(value: boolean | null | undefined): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return NOT_MEASURED;
}

export const TITLE_SEARCH_EMPTY_COPY: CollateralEmptyCopyConfig = {
  failedTitle: 'Title searches could not be read',
  filteredTitle: 'No title searches match the filters',
  emptyTitle: 'No title searches recorded',
  emptyReason: LIEN_DATA_REFUSAL_REASON,
};

export const TITLE_SEARCH_FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: labelOptions(TITLE_SEARCH_STATUS_LABEL),
  },
];

export interface TitleSearchSummaryState {
  rows: number;
  byStatus: Record<TitleSearchStatus, number>;
  insurable: number;
  /** Rows whose isInsurable is null, so the rows less the insurable count is not read as uninsurable. */
  insurabilityNotMeasured: number;
}

export function summarizeTitleSearches(searches: TitleSearch[]): TitleSearchSummaryState {
  const byStatus: Record<TitleSearchStatus, number> = {
    CLEAR: 0,
    COMPLETE: 0,
    IN_PROGRESS: 0,
    ISSUES_FOUND: 0,
    FAILED: 0,
  };
  let insurable = 0;
  let insurabilityNotMeasured = 0;
  for (const search of searches) {
    if (Object.prototype.hasOwnProperty.call(byStatus, search.status)) {
      byStatus[search.status] += 1;
    }
    if (search.isInsurable === true) insurable += 1;
    else if (search.isInsurable == null) insurabilityNotMeasured += 1;
  }
  return { rows: searches.length, byStatus, insurable, insurabilityNotMeasured };
}

/**
 * The current owner, or why there is none. ATTOM omits the owner name when it
 * holds none (KG 2026-09-27), so an absent owner on a finished search is not
 * "Pending".
 */
function ownerText(search: TitleSearch): string {
  if (search.currentOwner) return search.currentOwner;
  return search.status === 'IN_PROGRESS' ? 'Pending' : 'Not reported';
}

/** A lien total, or {@link LIEN_DATA_ABSENT} when the record carries none. */
export function formatLienAmount(value: number | null | undefined): string {
  return isMeasured(value) ? formatMoney(value) : LIEN_DATA_ABSENT;
}

function addressLine(search: TitleSearch): string {
  const a = search.propertyAddress;
  return [a?.city, [a?.state, a?.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

function useColumns(
  names: ReadonlyMap<string, TenantNameState>,
  onView: (search: TitleSearch) => void,
): ColumnDef<TitleSearch, unknown>[] {
  return useMemo<ColumnDef<TitleSearch, unknown>[]>(
    () => [
      {
        id: 'property',
        header: 'Property',
        accessorFn: (row) => row.propertyAddress?.street ?? '',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-sans font-medium text-primary leading-normal">
              {row.original.propertyAddress?.street ?? ABSENT}
            </div>
            <div className="text-xs font-sans text-tertiary leading-normal">
              {addressLine(row.original)}
            </div>
          </div>
        ),
        size: 220,
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (row) => ownerText(row),
        cell: ({ row }) => (
          <div>
            <div className={`text-sm font-sans leading-normal ${row.original.currentOwner ? 'text-primary' : 'text-tertiary'}`}>
              {ownerText(row.original)}
            </div>
            {row.original.ownershipType && (
              <div className="text-xs font-sans text-tertiary leading-normal">
                {row.original.ownershipType}
              </div>
            )}
          </div>
        ),
        size: 180,
      },
      {
        id: 'liens',
        header: 'Liens',
        accessorFn: (row) => row.lienCount ?? -1,
        cell: ({ row }) => (
          <div>
            <div className={`text-sm font-sans leading-normal ${isMeasured(row.original.lienCount) ? 'text-primary' : 'text-tertiary'}`}>
              {formatLienCount(row.original.lienCount)}
            </div>
            {isMeasured(row.original.lienCount) && row.original.lienCount > 0 && (
              <div className="text-xs font-mono text-tertiary tabular-nums leading-normal">
                {formatLienAmount(row.original.totalLienAmount)}
              </div>
            )}
          </div>
        ),
        size: 140,
      },
      {
        accessorKey: 'isInsurable',
        header: 'Insurable',
        cell: ({ row }) => {
          const value = row.original.isInsurable;
          const tone = value === true ? 'text-primary' : value === false ? 'text-error' : 'text-tertiary';
          return (
            <span className={`text-sm font-sans ${tone}`}>
              {insurabilityText(value)}
            </span>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <div>
            <TitleSearchStatusBadge status={row.original.status} />
            {isMeasured(row.original.defectCount) && row.original.defectCount > 0 && (
              <div className="text-error text-xs mt-1">
                {row.original.defectCount} {row.original.defectCount === 1 ? 'defect' : 'defects'}
              </div>
            )}
            {!isMeasured(row.original.defectCount) && row.original.status !== 'IN_PROGRESS' && (
              <div className="text-tertiary text-xs mt-1">Defects not measured</div>
            )}
          </div>
        ),
        size: 140,
      },
      {
        id: 'tenant',
        header: 'Tenant',
        accessorFn: (row) => tenantDisplayName(row.tenantId, names),
        cell: ({ row }) => (
          <TenantLoanCell tenantId={row.original.tenantId} loanId={row.original.loanId} names={names} />
        ),
        size: 180,
      },
      {
        accessorKey: 'searchDate',
        header: 'Date',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDate(getValue() as string | undefined)}
          </span>
        ),
        size: 120,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onView(row.original);
            }}
            className="p-1 hover:bg-recessed rounded transition-colors"
            aria-label={`View title search details for ${row.original.propertyAddress?.street ?? row.original.id}`}
            title="View details"
          >
            <ViewIcon size={20} className="text-secondary" />
          </button>
        ),
        size: 80,
      },
    ],
    [names, onView],
  );
}

const INPUT_CLASS =
  'w-full border border-recessed rounded px-3 py-2 text-sm bg-transparent outline-none focus:border-accent';

export function TitleSearchPage() {
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newSearchDialogOpen, setNewSearchDialogOpen] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState<TitleSearch | null>(null);

  // New search form state
  const [formStreet, setFormStreet] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formState, setFormState] = useState('');
  const [formZipCode, setFormZipCode] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);

  const [showDefects, setShowDefects] = useState(false);

  // One unfiltered read, so the status cards do not print a measured 0 for
  // every status outside an applied filter.
  const { data: searchesData, isLoading, error, refetch, isFetching } = useTitleSearches({
    limit: LIST_LIMIT,
  });
  const { mutate: createSearch, isPending: isCreating } = useCreateTitleSearch();

  const searches = useMemo(() => searchesData?.searches ?? [], [searchesData]);
  const tenantNames = useTenantNames(searches.map((s) => s.tenantId));
  const summary = useMemo(() => summarizeTitleSearches(searches), [searches]);
  const total = searchesData?.total ?? searches.length;
  const partialRead = total > searches.length;

  const statusFilter = activeFilters.find((f) => f.filterId === 'status')?.value;

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return searches.filter((s) => {
      if (statusFilter && s.status !== statusFilter) return false;
      if (!query) return true;
      const haystack = [
        s.propertyAddress?.street,
        s.propertyAddress?.city,
        s.propertyAddress?.state,
        s.propertyAddress?.zipCode,
        s.currentOwner,
        s.loanId,
        s.tenantId ? tenantDisplayName(s.tenantId, tenantNames) : undefined,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [searches, statusFilter, searchQuery, tenantNames]);

  const handleView = useCallback((search: TitleSearch) => {
    setRetryError(null);
    setShowDefects(false);
    setSelectedSearch(search);
  }, []);
  const columns = useColumns(tenantNames, handleView);

  const countNote = partialRead
    ? `Of the ${formatCount(searches.length)} most recent of ${formatCount(total)}`
    : undefined;
  const insurableNote =
    [
      countNote,
      summary.insurabilityNotMeasured > 0
        ? `${formatCount(summary.insurabilityNotMeasured)} not measured`
        : undefined,
    ]
      .filter(Boolean)
      .join('. ') || undefined;

  return (
    <div>
      <PageHeader
        description="Title search and ownership records from ATTOM, through the Reference Model"
        actions={
          <>
            <Button
              variant="secondary"
              icon={RefreshIcon}
              iconPosition="left"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              icon={TitleIcon}
              iconPosition="left"
              onClick={() => {
                setFormStreet('');
                setFormCity('');
                setFormState('');
                setFormZipCode('');
                setFormError(null);
                setNewSearchDialogOpen(true);
              }}
            >
              New Search
            </Button>
          </>
        }
      />

      {error && (
        <Alert type="error" title="Title searches could not be read" className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <span>{error.message}</span>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
              <Skeleton className="h-3 w-24 mb-3" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))
        ) : error ? (
          <>
            <MetricCard label="Total Searches" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Clear Titles" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="In Progress" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Issues Found" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Insurable" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          <>
            <MetricCard label="Total Searches" value={formatCount(total)} />
            <MetricCard label="Clear Titles" value={formatCount(summary.byStatus.CLEAR)} description={countNote} />
            <MetricCard label="In Progress" value={formatCount(summary.byStatus.IN_PROGRESS)} description={countNote} />
            <MetricCard label="Issues Found" value={formatCount(summary.byStatus.ISSUES_FOUND)} description={countNote} />
            <MetricCard label="Insurable" value={formatCount(summary.insurable)} description={insurableNote} />
          </>
        )}
      </div>

      <FilterBar
        filters={TITLE_SEARCH_FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by address, owner, loan ID, or tenant..."
        className="rounded-t-sm"
      />

      {isLoading ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<TitleSearch>
          data={filtered}
          columns={columns}
          enablePagination
          defaultPageSize={25}
          hoverable
          getRowId={(row) => row.id}
          emptyState={
            <CollateralListEmptyState
              copy={TITLE_SEARCH_EMPTY_COPY}
              readFailed={!!error}
              filteredOut={!error && searches.length > 0}
            />
          }
          className="w-full rounded-b-sm shadow-sm"
        />
      )}

      {/* New Search Dialog */}
      <Dialog
        open={newSearchDialogOpen}
        onOpenChange={(open) => { if (!open) setNewSearchDialogOpen(false); }}
        title="Initiate Title Search"
        size="md"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setNewSearchDialogOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isCreating}
              onClick={() => {
                setFormError(null);
                if (!formStreet.trim() || !formCity.trim() || !formState.trim() || !formZipCode.trim()) {
                  setFormError('All address fields are required.');
                  return;
                }
                createSearch(
                  {
                    address: {
                      street: formStreet.trim(),
                      city: formCity.trim(),
                      state: formState.trim(),
                      zipCode: formZipCode.trim(),
                    },
                  },
                  {
                    onSuccess: () => {
                      setNewSearchDialogOpen(false);
                    },
                    onError: (err: Error) => {
                      setFormError(err.message ?? 'Failed to initiate title search.');
                    },
                  }
                );
              }}
            >
              {isCreating ? 'Initiating...' : 'Initiate Search'}
            </Button>
          </>
        }
      >
        <div className="grid gap-4">
          {formError && <Alert type="error">{formError}</Alert>}
          <div>
            <label className="block text-xs text-tertiary mb-1" htmlFor="form-street">
              Street Address
            </label>
            <input
              id="form-street"
              type="text"
              value={formStreet}
              onChange={(e) => setFormStreet(e.target.value)}
              placeholder="123 Main St"
              className={INPUT_CLASS}
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-tertiary mb-1" htmlFor="form-city">
                City
              </label>
              <input
                id="form-city"
                type="text"
                value={formCity}
                onChange={(e) => setFormCity(e.target.value)}
                placeholder="Phoenix"
                className={INPUT_CLASS}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-tertiary mb-1" htmlFor="form-state">
                State
              </label>
              <input
                id="form-state"
                type="text"
                value={formState}
                onChange={(e) => setFormState(e.target.value.toUpperCase())}
                placeholder="AZ"
                maxLength={2}
                className={INPUT_CLASS}
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-tertiary mb-1" htmlFor="form-zip">
              ZIP Code
            </label>
            <input
              id="form-zip"
              type="text"
              inputMode="numeric"
              value={formZipCode}
              onChange={(e) => setFormZipCode(e.target.value)}
              placeholder="85001"
              maxLength={10}
              className={INPUT_CLASS}
              required
            />
          </div>
        </div>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog
        open={!!selectedSearch}
        onOpenChange={(open) => { if (!open) { setSelectedSearch(null); setShowDefects(false); } }}
        title="Title Search Details"
        size="lg"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setSelectedSearch(null)}>
              Close
            </Button>
            {selectedSearch?.status === 'FAILED' && (
              <Button
                variant="primary"
                icon={RefreshIcon}
                iconPosition="left"
                disabled={isCreating}
                onClick={() => {
                  const addr = selectedSearch.propertyAddress;
                  if (!addr?.street || !addr.city || !addr.state || !addr.zipCode) {
                    setRetryError('This search has no complete address to request again.');
                    return;
                  }
                  setRetryError(null);
                  createSearch(
                    {
                      address: {
                        street: addr.street,
                        city: addr.city,
                        state: addr.state,
                        zipCode: addr.zipCode,
                      },
                    },
                    {
                      onSuccess: () => {
                        setSelectedSearch(null);
                        refetch();
                      },
                      onError: (err: Error) => {
                        setRetryError(err.message);
                      },
                    }
                  );
                }}
              >
                {isCreating ? 'Retrying...' : 'Retry Search'}
              </Button>
            )}
            {selectedSearch?.status === 'ISSUES_FOUND' && (
              <Button
                variant="secondary"
                onClick={() => setShowDefects(prev => !prev)}
                aria-expanded={showDefects}
              >
                {showDefects ? 'Hide Defects' : 'View Defects'}
              </Button>
            )}
          </>
        }
      >
        {selectedSearch && (
          <>
            {retryError && <Alert type="error" className="mb-4">{retryError}</Alert>}

            {selectedSearch.status === 'ISSUES_FOUND' && (
              <Card className="p-4 mb-4 bg-error/10 border border-error/20">
                <div className="flex items-start gap-2">
                  <WarningIcon className="text-error flex-shrink-0" size={20} />
                  <div>
                    <p className="text-error font-medium">Title Issues Detected</p>
                    <p className="text-error/80 text-sm">
                      {isMeasured(selectedSearch.defectCount)
                        ? `${selectedSearch.defectCount} ${selectedSearch.defectCount === 1 ? 'defect' : 'defects'} found.`
                        : 'Defect count not measured.'}{' '}
                      Manual review required before proceeding.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Only a reported false warns; a null insurability was not measured. */}
            {selectedSearch.isInsurable === false && selectedSearch.status !== 'IN_PROGRESS' && (
              <Card className="p-4 mb-4 bg-warning/10 border border-warning/20">
                <div className="flex items-start gap-2">
                  <WarningIcon className="text-warning flex-shrink-0" size={20} />
                  <p className="text-warning">
                    This title is currently not insurable. Resolve outstanding issues first.
                  </p>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <p className="text-xs text-tertiary">Property Address</p>
                <p className="font-medium">
                  {[selectedSearch.propertyAddress?.street, addressLine(selectedSearch)].filter(Boolean).join(', ')}
                </p>
              </div>
              <div>
                <p className="text-xs text-tertiary">Current Owner</p>
                <p className="font-medium">{ownerText(selectedSearch)}</p>
              </div>
              <div>
                <p className="text-xs text-tertiary">Ownership Type</p>
                {selectedSearch.ownershipType ? (
                  <Badge variant="neutral">{selectedSearch.ownershipType}</Badge>
                ) : (
                  <p className="text-tertiary">Not reported</p>
                )}
              </div>
              <div>
                <p className="text-xs text-tertiary">Lien Count</p>
                <p className="font-medium">
                  {isMeasured(selectedSearch.lienCount) ? selectedSearch.lienCount : LIEN_DATA_ABSENT}
                </p>
              </div>
              <div>
                <p className="text-xs text-tertiary">Total Lien Amount</p>
                <p className="font-medium">{formatLienAmount(selectedSearch.totalLienAmount)}</p>
              </div>
              <div>
                <p className="text-xs text-tertiary">Insurable</p>
                {selectedSearch.isInsurable === true ? (
                  <Badge variant="success">Yes</Badge>
                ) : selectedSearch.isInsurable === false ? (
                  <Badge variant="error">No</Badge>
                ) : (
                  <p className="text-tertiary">{NOT_MEASURED}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-tertiary">Defects</p>
                {isMeasured(selectedSearch.defectCount) ? (
                  <p className={selectedSearch.defectCount > 0 ? 'text-error font-medium' : ''}>
                    {selectedSearch.defectCount}
                  </p>
                ) : (
                  <p className="text-tertiary">{NOT_MEASURED}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-tertiary">Status</p>
                <TitleSearchStatusBadge status={selectedSearch.status} />
              </div>
              <div>
                <p className="text-xs text-tertiary">Search Date</p>
                <p>{formatDateTime(selectedSearch.searchDate)}</p>
              </div>
              <div>
                <p className="text-xs text-tertiary">Collateral ID</p>
                <p className="font-mono text-sm">{selectedSearch.collateralId ?? ABSENT}</p>
              </div>
              <div>
                <p className="text-xs text-tertiary">Tenant and Loan</p>
                <TenantLoanCell
                  tenantId={selectedSearch.tenantId}
                  loanId={selectedSearch.loanId}
                  names={tenantNames}
                />
              </div>
            </div>

            {showDefects && selectedSearch.status === 'ISSUES_FOUND' && (
              <div className="mt-4">
                <BodyText variant="ui" className="font-semibold mb-2">Title Defects</BodyText>
                <Card className="p-4 bg-error/5 border border-error/10">
                  <div className="flex items-center gap-3">
                    <p className={`text-2xl font-semibold ${isMeasured(selectedSearch.defectCount) ? 'text-error' : 'text-tertiary'}`}>
                      {isMeasured(selectedSearch.defectCount) ? selectedSearch.defectCount : ABSENT}
                    </p>
                    <div>
                      <p className="font-medium text-sm">
                        {isMeasured(selectedSearch.defectCount)
                          ? `${selectedSearch.defectCount === 1 ? 'Defect' : 'Defects'} identified`
                          : 'Defect count not measured'}
                      </p>
                      <p className="text-sm text-tertiary">
                        {selectedSearch.isInsurable === true
                          ? 'Title is insurable with exceptions.'
                          : selectedSearch.isInsurable === false
                            ? 'Title is not insurable.'
                            : 'Insurability not measured.'}{' '}
                        Manual review required.
                      </p>
                    </div>
                  </div>
                  {isMeasured(selectedSearch.lienCount) && selectedSearch.lienCount > 0 && (
                    <div className="mt-3 pt-3 border-t border-error/10">
                      <p className="text-sm">
                        {formatLienCount(selectedSearch.lienCount)} totaling{' '}
                        {formatLienAmount(selectedSearch.totalLienAmount)}
                      </p>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </>
        )}
      </Dialog>
    </div>
  );
}
